"use client"

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Twitter, Facebook, Linkedin, Copy, Check, Gift } from 'lucide-react'
import { useState, useEffect } from 'react'
import { createShareLink, trackShare, getShareStats } from '../service'

interface ShareToolSectionProps {
  email?: string | null
  userId?: string | null
}

export function ShareToolSection({ email, userId }: ShareToolSectionProps) {
  const [copied, setCopied] = useState(false)
  const [referralCode, setReferralCode] = useState<string | null>(null)
  const [shareUrl, setShareUrl] = useState<string>('')
  const [shareStats, setShareStats] = useState<{ shareCount: number; signupCount: number } | null>(null)
  const shareText = "Just completed my 2025 review with @planamiapp. Summarized my experience, memories and prepared for the next year."

  // Generate or get referral code on mount
  useEffect(() => {
    const initShare = async () => {
      if (!email && !userId) return
      
      try {
        const result = await createShareLink(email || undefined, userId || undefined)
        if (result.success && result.referralCode) {
          setReferralCode(result.referralCode)
          setShareUrl(result.shareUrl)
        }
      } catch (error) {
        console.error('Error creating share link:', error)
      }
    }
    
    initShare()
  }, [email, userId])

  // Load share stats if authenticated
  useEffect(() => {
    if (userId) {
      getShareStats(userId).then(stats => {
        if (stats) setShareStats(stats)
      })
    }
  }, [userId])

  const handleCopy = async () => {
    if (!shareUrl) return
    
    await navigator.clipboard.writeText(shareUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    
    // Track share
    if (referralCode) {
      await trackShare(referralCode, 'copy')
    }
  }

  const handleShare = async (platform: 'twitter' | 'facebook' | 'linkedin') => {
    if (!shareUrl) return
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    }
    
    window.open(urls[platform], '_blank', 'width=600,height=400')
    
    // Track share
    if (referralCode) {
      await trackShare(referralCode, platform)
    }
  }

  if (!shareUrl) return null

  return (
    <div className="mt-4 pt-8 border-t border-border/50">
      <div className="text-center mb-3">
        <p className="text-2xl font-medium mb-1">Loved this? Share the review tool with friends</p>
        <div className="flex items-center justify-center gap-2 mb-2">
          <Gift className="h-4 w-4 text-primary" />
          <p className="text-base text-muted-foreground">
            Get <span className="font-semibold text-primary">2 extra weeks</span> of free Planami premium trial for each friend who signs up!
          </p>
        </div>
        {shareStats && shareStats.signupCount > 0 && (
          <Badge variant="secondary" className="text-xs">
            {shareStats.signupCount} friend{shareStats.signupCount !== 1 ? 's' : ''} signed up • +{shareStats.signupCount * 2} weeks added!
          </Badge>
        )}
      </div>
      <div className="flex justify-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleShare('twitter')}
          className="h-8 w-8 p-0"
          title="Share on Twitter"
        >
          <Twitter className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleShare('facebook')}
          className="h-8 w-8 p-0"
          title="Share on Facebook"
        >
          <Facebook className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleShare('linkedin')}
          className="h-8 w-8 p-0"
          title="Share on LinkedIn"
        >
          <Linkedin className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="h-8 w-8 p-0"
          title="Copy link"
        >
          {copied ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  )
}

